import { extendTheme } from "native-base";
import { Platform } from "react-native";

// RissApp brand palette — aligned to the rissapp.ch design tokens:
//   ink   #112127 (ink-700 #0c1a1f)  — text / dark surfaces
//   mint  #7ddc9e (mint-600 #6bcb8d) — primary accent / button fills (dark text on top)
//   moss  #2f7d54 (moss-500 #2f9e6f) — readable green for accent text, icons, marks
//   paper #f3f6f3                     — off-white page background
const defaultColors = {
  // mint — primary accent. `500` is exact brand mint so native-base solid
  // buttons render mint with auto-selected dark text; `600` is the hover/pressed.
  primary: {
    "50": "#eefaf3",
    "100": "#d5f3e0",
    "200": "#bcedcd",
    "300": "#a7e6bf",
    "400": "#90e0ae",
    "500": "#7ddc9e", // mint
    "600": "#6bcb8d", // mint-600
    "700": "#57b579",
    "800": "#419560",
    "900": "#2c6b45",
  },
  // moss — deep readable green for accent text / icons on white (passes contrast)
  moss: {
    "50": "#eaf5ef",
    "100": "#cfe8db",
    "200": "#a9d6bf",
    "300": "#7cbf9d",
    "400": "#4fa87c",
    "500": "#2f9e6f", // moss-500
    "600": "#2f7d54", // moss
    "700": "#276646",
    "800": "#1f4f37",
    "900": "#163a29",
  },
  // ink — dark near-black teal, text and dark surfaces
  ink: {
    "50": "#e7eaeb",
    "100": "#c2c9cb",
    "200": "#9aa4a7",
    "300": "#6f7d81",
    "400": "#465257",
    "500": "#2a373c",
    "600": "#1c2b30",
    "700": "#152329",
    "800": "#112127", // ink
    "900": "#0c1a1f", // ink-700
  },
  // neutral grays; `50` is the brand paper background
  gray: {
    "50": "#f3f6f3", // paper
    "100": "#e5e9e6",
    "200": "#cdd4ce",
    "300": "#aeb6b0",
    "400": "#8b9590",
    "500": "#6f7973",
    "600": "#565f5a",
    "700": "#3f4744",
    "800": "#2b3230",
    "900": "#1a201e",
  },
  paper: "#f3f6f3",
  // Dark end of the brand gradient (see HeaderGradient); used for the measure
  // and edit icons. Keep in sync with `deepGreen` in colors.ts.
  deepGreen: "#165147",
};

// Mg¹² (atipo), the rissapp.ch brand typeface. Native only.
//
// The browser picks a face out of the single @font-face family in global.css
// from `font-weight`/`font-style`, but React Native never synthesises a weight:
// each face is a separately registered family (App.tsx loads them through
// expo-font), and on Android passing fontFamily *and* fontWeight together
// silently falls back to the system font. `fontConfig` is native-base's mapping
// from family + weight to those per-face names; useResolvedFontFamily drops
// fontWeight/fontStyle once it has resolved one.
//
// Every weight has to be listed: an unmapped one resolves to `undefined` and the
// text renders in the system font. The values are plain strings rather than
// `{ normal, italic }` objects because only the three uprights are bundled — the
// UI uses no italics — so italic text stays roman. Add the *Italic.otf faces and
// switch the entries to objects if that ever changes.
//
// This needs patches/native-base+3.5.0-rc.7.patch to take effect at all: stock
// 3.5.0-rc.7 loses the component-theme `fontFamily` before useResolvedFontFamily
// runs, so nothing below is ever consulted and every label renders in the
// platform font. Drop the patch and the app silently reverts to San Francisco.
const nativeFontConfig = {
  Mg: {
    100: "Mg-Regular",
    200: "Mg-Regular",
    300: "Mg-Regular",
    400: "Mg-Regular",
    500: "Mg-Medium",
    600: "Mg-Medium",
    700: "Mg-Bold",
    800: "Mg-Bold",
    900: "Mg-Bold",
    950: "Mg-Bold", // native-base's `extraBlack`
  },
};

export const theme = extendTheme({
  colors: defaultColors,
  // native-base resolves Text/Heading/Input's `fontFamily: 'body' | 'heading'`
  // through this map. Without it react-native-web falls back to its own system
  // stack and the CSS on <body> never reaches those nodes, and native keeps the
  // platform font. `fontConfig` is deliberately web-free: there the family is
  // literally "Mg" for every weight, and pointing it at the per-face names would
  // ask the browser for families that no @font-face declares.
  fonts: {
    heading: "Mg",
    body: "Mg",
    // "Mg" is a key here as well as a value so that a call site passing
    // `fontFamily="Mg"` directly still reaches `fontConfig`: useResolvedFontFamily
    // only consults it when its input is itself a key of this scale.
    Mg: "Mg",
  },
  ...(Platform.OS === "web" ? {} : { fontConfig: nativeFontConfig }),
  components: {
    // Pill-shaped buttons throughout, matching the rissapp.ch design language.
    // Overriding `baseStyle` (not `defaultProps`): native-base resolves props
    // as defaultProps -> baseStyle -> variant -> call-site props, so a
    // defaultProps value would be overwritten by the built-in `borderRadius:
    // 'sm'`. extendTheme's customizer merges this object over the result of
    // native-base's function-based baseStyle, and an explicit `borderRadius`
    // at the call site still wins over both.
    Button: {
      baseStyle: {
        borderRadius: "full",
      },
    },
    // Button.Group spreads its own `borderRadius: 4` onto its children as a
    // real prop, which would otherwise square off the buttons in the alert
    // dialogs (ForceSync, CleanupMediaLibrary).
    ButtonGroup: {
      defaultProps: {
        borderRadius: "full",
      },
    },
    IconButton: {
      baseStyle: {
        borderRadius: "full",
      },
    },
    // Dividers in brand mint instead of native-base's neutral `muted.300`.
    Divider: {
      baseStyle: {
        bg: "primary.500",
        _dark: {
          bg: "primary.500",
        },
      },
    },
  },
});
